const audioCache = new Map<string, HTMLAudioElement>();

export type SoundName = "message" | "notification" | "join_call" | "leave_call" | "stream_start" | "stream_end" | "call" | "mute" | "unmute";

function getAudio(name: SoundName): HTMLAudioElement {
  let audio = audioCache.get(name);
  if (!audio) {
    audio = new Audio(`/assets/sounds/${name}.mp3`);
    audio.preload = "auto";
    audioCache.set(name, audio);
  }
  return audio;
}

/**
 * Play a notification sound by name
 */
export function playSound(name: SoundName) {
  const audio = getAudio(name);
  audio.currentTime = 0;
  audio.play().catch(() => {});
}

// --- Web Audio API for loop sounds (survives autoplay policy) ---
let audioCtx: AudioContext | null = null;
const bufferCache = new Map<string, AudioBuffer>();
let loopSource: AudioBufferSourceNode | null = null;
let loopTimeout: ReturnType<typeof setTimeout> | null = null;
let loopGeneration = 0;

// Track which channel is currently ringing
let _ringingChannelId: string | null = null;
let _ringingGeneration = 0;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

/**
 * Resume the AudioContext so it can play audio from non-gesture contexts.
 * Must be called from a user gesture handler (click, keydown, etc).
 */
export function unlockAudio() {
  const ctx = getAudioContext();
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
}

async function loadAudioBuffer(name: SoundName): Promise<AudioBuffer> {
  const cached = bufferCache.get(name);
  if (cached) return cached;

  const response = await fetch(`/assets/sounds/${name}.mp3`, { cache: "no-store" });
  const arrayBuffer = await response.arrayBuffer();
  const buffer = await getAudioContext().decodeAudioData(arrayBuffer);
  bufferCache.set(name, buffer);
  return buffer;
}

export function getRingingChannelId(): string | null {
  return _ringingChannelId;
}

export function getRingingGeneration(): number {
  return _ringingGeneration;
}

export function setRingingChannelId(id: string | null) {
  _ringingChannelId = id;
  if (id !== null) _ringingGeneration++;
}

/**
 * Play a sound in a loop for a given duration using Web Audio API.
 * AudioContext stays unlocked after a single user gesture, so subsequent
 * calls from WebSocket events will play without needing new gestures.
 */
export function playSoundLoop(name: SoundName, durationMs = 60_000) {
  // Stop any existing loop
  stopLoopSource();

  if (loopTimeout) {
    clearTimeout(loopTimeout);
    loopTimeout = null;
  }

  const thisGeneration = ++loopGeneration;
  const ctx = getAudioContext();

  // Try to resume if suspended (best-effort, may need user gesture)
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  function startLoop(buffer: AudioBuffer) {
    if (loopGeneration !== thisGeneration) {
      console.info("[call-ring] Audio loaded but loop was cancelled");
      return;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(ctx.destination);
    source.start();
    loopSource = source;
    console.info("[call-ring] Audio playing successfully");
  }

  // Use cached buffer synchronously to avoid race conditions
  // when playSoundLoop is called multiple times in quick succession
  const cached = bufferCache.get(name);
  if (cached) {
    startLoop(cached);
  } else {
    loadAudioBuffer(name).then(startLoop).catch((err) => {
      console.error("[call-ring] Audio play failed:", err);
    });
  }

  loopTimeout = setTimeout(() => {
    stopSoundLoop();
  }, durationMs);
}

function stopLoopSource() {
  if (loopSource) {
    try {
      loopSource.stop();
    } catch {
      // Already stopped
    }
    loopSource.disconnect();
    loopSource = null;
  }
}

/**
 * Stop the currently looping sound and clear ringing state
 */
export function stopSoundLoop() {
  console.info("[call-ring] stopSoundLoop called, ringingChannel=", _ringingChannelId, new Error().stack?.split('\n').slice(1, 4).join(' <- '));
  loopGeneration++;

  if (loopTimeout) {
    clearTimeout(loopTimeout);
    loopTimeout = null;
  }

  stopLoopSource();
  _ringingChannelId = null;
}
